import * as React from 'react';
import PageModel from '@app/Model/PageModel';
import { Button, PageSection, TextContent, Text, TextVariants, Title, Alert } from '@patternfly/react-core';
import { reactFormatter, ReactTabulator } from 'react-tabulator';
import TabulatorCommonOptions from '@app/utils/TabulatorCommonOptions';
import { Link } from 'react-router-dom';
import UserFpuInterface from '@app/Interface/UserFpuInterface';
import UserInterface from '@app/Interface/UserInterface';
import ItemLoader from '@app/Components/ItemLoader';
import UserAuthentication from '@app/Security/UserAuthentication';
import ProducerHelper from '@app/Service/ProducerHelper';
import ProducerInterface from '@app/Interface/ProducerInterface';
import UserRoleTypesInterface from '@app/Interface/UserRoleTypesInterface';
import UserRoleTypesHelper from '@app/Service/UserRoleTypesHelper';
import MyFpuComponentViewModel from '@app/Model/MyFpuComponentViewModel';
import MyFpuComponentViewInterface from '@app/Interface/MyFpuComponentViewInterface';
import FpuAddresses from '@app/FpuAddresses/FpuAddresses';
import FpuPermissions from '@app/FpuPermissions/FpuPermissions';
import { PageRouteContext } from '@app/Context/PageRouteContext';
import PageRouteHelper from '@app/Service/PageRouteHelper';
import TemplateDefinitions from '@app/utils/TemplateDefinitions';
import htmlParser from '@app/utils/htmlParser';
import ProducerModel from '@app/Model/ProducerModel';

import SCTabulator from '@app/utils/SCTabulator';
import options from '@app/utils/SCTabulatorCommonOptions';

interface MyFpuListPageProps {
    pageData: PageModel,
    currentUser: UserInterface
}

const MyFpuListPage: React.FC<MyFpuListPageProps> = ({ pageData, currentUser, children }) => {

    const [fpuRows, setFpuRows] = React.useState<UserFpuInterface[]>([]);

    const [isLoading, setIsLoading] = React.useState(true);

    const initialProducers: ProducerInterface[] = [];

    const [producers, setProducers] = React.useState(initialProducers);

    const initialRoleTypes: UserRoleTypesInterface[] = [];

    const [roleTypes, setRoleTypes] = React.useState(initialRoleTypes);

    const defaultComponentView = new MyFpuComponentViewModel();

    const [componentToView, setComponentToview] = React.useState<MyFpuComponentViewInterface>(defaultComponentView);

    const { pageRouteState } = React.useContext(PageRouteContext);

    const notificationsPageRoute = PageRouteHelper.getPageRouteByTemplate(pageRouteState.pageRoutes, TemplateDefinitions.TEMPLATE_CONTACT_PAGE);

    /**
     * On button click, set the component we want to view
     * 
     * @param {} component FPU Component we want to view 
     */
    const buttonHandler = (component: MyFpuComponentViewInterface) => {

        setComponentToview(component);
    }

    /**
     * creates a button to load FpuAddressesForm
     * see: http://tabulator.info/docs/4.1/format#icon
     *      https://github.com/ngduc/react-tabulator/blob/master/src/ReactTabulatorExample.tsx#L23
     * 
     * @param cell 
     * @param formatterParams 
     * @param onRendered 
     * @returns 
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const FpuAddresssesButton = (props: any) => {

        const rowData: UserFpuInterface = props.cell._cell.row.data;

        let fpuId = Number(rowData.producerId);

        if (isNaN(fpuId)) {
            fpuId = 0;
        }

        // user must be master account holder to edit addresses
        if (fpuId && UserAuthentication.userHasMasterAccountRole(currentUser.viewPermissions, fpuId)) {

            const component = new MyFpuComponentViewModel();

            const producer = producers.find(item => item.producerId === fpuId) ?? new ProducerModel();

            component.fpuId = fpuId;

            component.producer = producer;

            component.component = "FpuAddresses";

            // how to pass parameter with onclick handler: https://upmostly.com/tutorials/pass-a-parameter-through-onclick-in-react
            return (
                <Button
                    variant="primary"
                    isSmall
                    onClick={() => buttonHandler(component)}
                >Addresses</Button>
            );

        } else {

            return <></>;

        }

    }

    /**
     * creates a button to load FpuAddressesForm
     * see: http://tabulator.info/docs/4.1/format#icon
     *      https://github.com/ngduc/react-tabulator/blob/master/src/ReactTabulatorExample.tsx#L23
     * 
     * @param cell 
     * @param formatterParams 
     * @param onRendered 
     * @returns 
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const FpuPermissionsButton = (props: any) => {

        const rowData: UserFpuInterface = props.cell._cell.row.data;

        let fpuId = Number(rowData.producerId);

        if (isNaN(fpuId)) {
            fpuId = 0;
        }

        // check if user can grant permissions for FPU
        if (fpuId && UserAuthentication.userCanGrantAccess(currentUser.viewPermissions, fpuId)) {

            const component = new MyFpuComponentViewModel();

            component.fpuId = fpuId;

            const producer = producers.find(item => item.producerId === fpuId) ?? new ProducerModel();

            component.producer = producer;

            component.component = "FpuPermissions";

            // how to pass parameter with onclick handler: https://upmostly.com/tutorials/pass-a-parameter-through-onclick-in-react
            return (
                <Button
                    variant="primary"
                    isSmall
                    onClick={() => buttonHandler(component)}
                >Permissions</Button>
            );
        } else {

            return <></>;

        }

    }

    const tabulatorColumns = [
        { title: "FPU #", field: "producerId", minWidth: 70 },
        { title: "Name", field: "producerName", minWidth: 70 },
        { title: "Your Roles", field: "userRoles", minWidth: 70 },
        { formatter: reactFormatter(<FpuAddresssesButton />), minWidth: 70 },
        { formatter: reactFormatter(<FpuPermissionsButton />), minWidth: 70 }
    ];

    const tabulatorOptions = {
        headerSort: false, //disable header sort for all columns
    };

    // get a list of user role types
    React.useEffect(() => {

        const controller = new AbortController();

        let mounted = true;

        // TODO: caching
        const getUserRoleTypes = async () => {

            const roleTypes = await UserRoleTypesHelper.getRoleTypes(controller);

            if (mounted) {
                setRoleTypes(roleTypes);
            }

        }

        getUserRoleTypes();

        return () => {
            mounted = false;
            controller?.abort();
        };

    }, []);

    // get a list of producers for the logged in user
    React.useEffect(() => {

        const controller = new AbortController();

        let mounted = true;

        // TODO: caching
        const getUserProducers = async () => {

            const producers = await ProducerHelper.getProducers(controller);

            if (mounted) {
                setProducers(producers);
            }
        }

        getUserProducers();

        return () => {
            mounted = false;
            controller?.abort();
        };

    }, []);

    // get fpuRows
    React.useEffect(() => {

        function getUserFpuRows() {

            const rows: UserFpuInterface[] = [];

            // loop through the user roles
            if (currentUser.fpuRoles !== undefined && typeof currentUser.fpuRoles !== "boolean") {

                currentUser.fpuRoles.forEach(role => {

                    const producer = producers.find((producer) => { return producer.producerId === Number(role.fpu) });

                    const producerName = producer?.alphaName ?? "";

                    const roleNamesList = UserRoleTypesHelper.getRoleNamesList(roleTypes, role.fpu_roles);

                    const roleNames = roleNamesList.join(", ");

                    const row: UserFpuInterface = {
                        producerId: role.fpu,
                        producerName: producerName,
                        userRoles: roleNames
                    };

                    rows.push(row);

                });

                setFpuRows(rows);
            }

            setIsLoading(false);

        }

        setIsLoading(true);

        if (currentUser.id > 0 && producers.length && roleTypes.length) {
            getUserFpuRows();
        }

    }, [currentUser, producers, roleTypes]);

    if (componentToView.component === "FpuAddresses") {
        return (
            <FpuAddresses producerId={componentToView.fpuId} producer={componentToView.producer} buttonHandler={buttonHandler} />
        );
    }

    if (componentToView.component === "FpuPermissions") {
        return (
            <FpuPermissions producerId={componentToView.fpuId} producer={componentToView.producer} roleTypes={roleTypes} buttonHandler={buttonHandler} />
        );
    }

    return (
        <>
            <PageSection>
                <Title headingLevel="h1" className="pf-u-mb-md">{pageData.title}</Title>
                <div className="pf-c-content pf-u-mb-md">
                    {htmlParser.htmlToJSX(pageData.content)}
                </div>
                {isLoading
                    ? <ItemLoader />
                    :
                    <>
                        {fpuRows.length
                            ?
                            <SCTabulator
                                data={fpuRows}
                                columns={tabulatorColumns}
                                layout="fitColumns"
                                options={{
                                    ...options('nda-fpu-list-sc', false, false), ...tabulatorOptions
                                }}
                            />
                            :
                            <Alert
                                variant="warning"
                                title="No FPU(s) assigned"
                                aria-live="polite"
                                role="alert"
                                isInline
                            >
                                <p>
                                    You haven&apos;t been assigned any FPU roles. If you&apos;re a system administrator, you can use the content management system to manage
                                    FPU roles for users or you can assign a FPU role to yourself.
                                </p>
                            </Alert>

                        }
                        <TextContent className="pf-c-content" >
                            <Text component={TextVariants.p}>
                                <span>Don&apos;t see your FPU?</span>
                                <Link to={notificationsPageRoute.path} className="pf-u-px-sm">Click here to request access to another FPU.</Link>
                            </Text>
                        </TextContent>
                    </>
                }
                {children}
            </PageSection>
        </>
    )

};

export default MyFpuListPage;